import moment from 'moment';
import { useMemo } from 'react';
import { compose } from 'redux';

import withSelectedEnrolment from '../../../../common/hub/NewWithSelectedEnrolment';
import { PATH_HUB_CR_PAUSE_SESSIONS } from '../../../../util/pagePath';
import useTimezone from '../../../common/hooks/useTimezone';
import MultiStep from '../../../common/multi-step';
import api from '../../../legacy/api';
import { EnrolmentLayout } from '../../../legacy/components';
import { useGetSessionPolicy } from '../../../legacy/hooks';
import { Enrolment } from '../../../legacy/types';
import LoadingStateContainer from '../../pause-enrolment/LoadingStateContainer';
import { PauseEnrolmentProvider } from '../../pause-enrolment/PauseEnrolmentContext';
import { getPauseWindow } from '../../pause-enrolment/helpers';
import useInitializeData from '../../pause-enrolment/hooks/useInitializeData';
import useRedirects from '../../pause-enrolment/hooks/useRedirects';
import useSchoolPeriods from '../../pause-enrolment/hooks/useSchoolPeriods';
import CancelEnrolmentSuccess from './CancelEnrolmentSuccess';
import SelectPauseDates from './form-steps/SelectPauseDates';
import SummerPauseAndDiscountSuccess from './form-steps/SummerPauseAndDiscountSuccess';

export type SummerPauseAndDiscountContextType = {
  enrolmentId: string;
};

type SelectedEnrolment = Enrolment & {
  resumeSessionDate?: string;
};

type SelectedEnrolmentProps = {
  selectedEnrolment: SelectedEnrolment;
};

export type SummerPauseAndDiscountProps = SelectedEnrolmentProps & {
  handleCancelEnrolment: (onSubmitSuccess: () => void, onSubmitFailed: () => void) => void;
};

const SummerPauseAndDiscount = (props: SummerPauseAndDiscountProps) => {
  useInitializeData();
  useRedirects(props);
  const { selectedEnrolment, handleCancelEnrolment } = props;
  const enrolmentId = selectedEnrolment.enrolmentSfid;
  const {
    data: sessionPolicy,
    isLoading: isLoadingSessionPolicy,
    error: sessionPolicyQueryError,
  } = useGetSessionPolicy({
    enrolmentId,
  });
  const isFlexi = sessionPolicy?.pause?.pauseAllowed === 'Anytime';

  const { data: upcomingSessions = [], isLoading: isLoadingUpcomingSessions } = api.session.all.useQuery('upcoming');
  const timezone = useTimezone();

  const { currentSchoolTerm, nextSchoolTerm, currentOrUpcomingHoliday } = useSchoolPeriods();

  const { maxDate: regularPauseMaxDate } = getPauseWindow(currentOrUpcomingHoliday);
  const pauseMaxDate = regularPauseMaxDate;
  const tomorrow = moment().add(1, 'day');

  const sectionHeading =
    selectedEnrolment.student && selectedEnrolment.subject
      ? `${selectedEnrolment.student} ${selectedEnrolment.subject}`
      : '';

  const steps = useMemo(
    () => [
      {
        id: 'select-pause-dates',
        component: (
          <EnrolmentLayout
            sectionHeading={sectionHeading}
            mainHeading="Taking a summer break?"
            enrolmentId={enrolmentId}
          >
            <SelectPauseDates handleCancelEnrolment={handleCancelEnrolment} />
          </EnrolmentLayout>
        ),
      },
      {
        id: 'summer-pause-and-discount-success',
        component: (
          <EnrolmentLayout>
            <SummerPauseAndDiscountSuccess />
          </EnrolmentLayout>
        ),
      },
      {
        id: 'cancel-enrolment-success',
        component: (
          <EnrolmentLayout>
            <CancelEnrolmentSuccess />
          </EnrolmentLayout>
        ),
      },
    ],
    [enrolmentId, sectionHeading, handleCancelEnrolment]
  );

  return (
    <LoadingStateContainer
      isLoading={isLoadingSessionPolicy || isLoadingUpcomingSessions}
      failedToLoad={!!sessionPolicyQueryError}
    >
      <PauseEnrolmentProvider
        initialState={{
          pauseMethod: !isFlexi ? 'pause-by-specific-date' : null,
          pauseResumptionMethod: 'resume-on-revision-period',
        }}
        currentSchoolTerm={currentSchoolTerm}
        currentOrUpcomingHoliday={currentOrUpcomingHoliday}
        nextSchoolTerm={nextSchoolTerm}
        selectedEnrolment={selectedEnrolment}
        sessions={upcomingSessions}
        sessionPolicy={sessionPolicy}
        timezone={timezone}
        pauseMinDate={tomorrow}
        pauseMaxDate={pauseMaxDate}
      >
        <MultiStep steps={steps} initialStep="select-pause-dates" />;
      </PauseEnrolmentProvider>
    </LoadingStateContainer>
  );
};

export default compose(withSelectedEnrolment(SummerPauseAndDiscount, null, PATH_HUB_CR_PAUSE_SESSIONS));
